import React from 'react';
import './ConfigurationTab.css';
import './EditConfigForm.css';
import DOMPurify from 'dompurify';

function EditConfigForm({ editingConfig, setEditingConfig, saveChanges, closeEditForm }) {
    const sanitizeAndValidateInput = (value, maxLength = 25) => {
        const sanitizedValue = DOMPurify.sanitize(value);

        // Validate length
        return sanitizedValue.length <= maxLength ? sanitizedValue : sanitizedValue.substring(0, maxLength);
    };

    // Helper function to handle the changes in the columns array
    const handleColumnChange = (index, field, value) => {
        const sanitizedValue = sanitizeAndValidateInput(value);
        const newColumns = [...editingConfig.columns];
        newColumns[index][field] = sanitizedValue;
        setEditingConfig({ ...editingConfig, columns: newColumns });
    };

    const handleThresholdChange = (value) => {
        const sanitizedValue = DOMPurify.sanitize(value);
        const numericValue = Math.min(100, Math.max(0, Number(sanitizedValue))); // Clamp value between 0 and 100
        setEditingConfig({ ...editingConfig, matchingThreshold: numericValue });
    };

    const handleUniqueIdentifierChange = (value) => {
        setEditingConfig({ ...editingConfig, uniqueIdentifierColumn: value });
    };

    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                saveChanges(editingConfig);
            }}
            className="edit-config-form"
        >
            <h2>Edit Configuration - {editingConfig.title}</h2>
            <div className="form-group">
                <label>Reconciliation Type:</label>
                <select
                    className="form-control"
                    value={editingConfig.type}
                    onChange={(e) => setEditingConfig({ ...editingConfig, type: e.target.value })}
                >
                    <option value="trade">Trade</option>
                    <option value="position">Position</option>
                    <option value="cash">Cash</option>
                    <option value="bank">Bank</option>
                    <option value="fee">Fee</option>
                    <option value="other">Other</option>
                    <option value="trade surveillance">Trade Surveillance</option>
                </select>
            </div>

            <div className="form-group">
                <label>Matching Threshold (0-100):</label>
                <input
                    type="number"
                    className="form-control"
                    value={editingConfig.matchingThreshold || ''}
                    min="0"
                    max="100"
                    onChange={(e) => handleThresholdChange(e.target.value)}
                />
            </div>

            <div className="form-group">
    <label>Automate Runs:</label>
    <select
        className="form-control"
        value={editingConfig.automateRuns || 'false'}
        onChange={(e) => setEditingConfig({ ...editingConfig, automateRuns: e.target.value })}
    >
        <option value="true">True</option>
        <option value="false">False</option>
    </select>
</div>

            <div className="form-group">
                <label>Unique Identifier Column:</label>
                <select
                    className="form-control"
                    value={editingConfig.uniqueIdentifierColumn || ''}
                    onChange={(e) => handleUniqueIdentifierChange(e.target.value)}
                >
                    <option value="">None</option>
                    {editingConfig.columns.map((column, index) => (
                        <option key={index} value={column.standardName}>
                            {column.standardName}
                        </option>
                    ))}
                </select>
            </div>

            {editingConfig.columns.map((column, i) => (
                <div key={i} className="column-group">
                    <div className="form-group">
                        <label>Standard Name:</label>
                        <input
                            type="text"
                            className="form-control"
                            value={column.standardName}
                            onChange={(e) => handleColumnChange(i, 'standardName', e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Name in File 1:</label>
                        <input
                            type="text"
                            className="form-control"
                            value={column.nameInFile1}
                            onChange={(e) => handleColumnChange(i, 'nameInFile1', e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Name in File 2:</label>
                        <input
                            type="text"
                            className="form-control"
                            value={column.nameInFile2}
                            onChange={(e) => handleColumnChange(i, 'nameInFile2', e.target.value)}
                        />
                    </div>

                    <div className="form-group">
                        <label>Data Type:</label>
                        <select
                            className="form-control"
                            value={column.dataType}
                            onChange={(e) => handleColumnChange(i, 'dataType', e.target.value)}
                        >
                            <option value="text">Text</option>
                            <option value="number">Number</option>
                            <option value="date">Date</option>
                            <option value="multi">Multi</option>
                        </select>
                    </div>

                    <div className="column-actions">
                        {editingConfig.columns.length !== 1 && (
                            <button
                                type="button"
                                className="remove-btn"
                                onClick={() => {
                                    const newColumns = [...editingConfig.columns];
                                    newColumns.splice(i, 1);
                                    setEditingConfig({ ...editingConfig, columns: newColumns });
                                }}
                            >
                                Remove
                            </button>
                        )}

                        {editingConfig.columns.length - 1 === i && (
                            <button
                                type="button"
                                className="add-btn"
                                onClick={() => {
                                    const newColumns = [
                                        ...editingConfig.columns,
                                        { standardName: '', nameInFile1: '', nameInFile2: '', dataType: 'text' },
                                    ];
                                    setEditingConfig({ ...editingConfig, columns: newColumns });
                                }}
                            >
                                Add
                            </button>
                        )}
                    </div>
                </div>
            ))}

            <div className="form-actions">
                <button type="submit" className="save-edits-btn">
                    Save Changes
                </button>
                <button type="button" className="cancel-btn" onClick={closeEditForm}>
                    Cancel
                </button>
            </div>
        </form>
    );
}

export default EditConfigForm;
